import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'

import ColumnAssignmentModal from 'common/components/dataUpload/ColumnAssignmentModal'
import {
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  allocationAnswerKey,
  allocationLineCounts,
  askableAllocations,
  normalizeAllocationQuestions,
  pendingQuestionsText,
  skippedAllocationAnswers,
  withoutAnsweredAllocations,
} from 'common/helpers/dataUpload'

import {useAllocationServer} from '../../hooks/useAllocationServer'
import {useVersiServer} from '../../hooks/useVersiServer'
import type {
  AllocationPrefillResult,
  AllocationProgress,
  AllocationRemovalPreview,
  AllocationRemovalRequest,
  AllocationServerError,
  AllocationServerGrid,
  AllocationServerScope,
} from '../../types/allocationServer'
import {
  classIdOf,
  countsOf,
  frequencyOf,
  scopeChoicesOf,
  scopeIdOf,
  splitSlotKey,
} from '../../utils/allocationServerAdapter'
import {allocationQuestionsForTeachers} from '../../utils/allocationQuestionsForTeachers'
import {type AllocationScopeQuery, allocationServerApi} from '../../utils/allocationServerApi'
import {ONBOARDING_QA_OCT18} from '../../utils/onboardingQaOct18'
import {
  type AllocationRow,
  type AllocationStatusFilter,
  formatFrequency,
  parseDuration,
  parseFrequency,
  slotKeyFor,
} from '../../utils/teacherAllocationModel'
import AllocationConfirmDialog from './AllocationConfirmDialog'
import AllocationGrid, {type AllocationGridFlash} from './AllocationGrid'
import AllocationLanding from './AllocationLanding'
import {AllocationFooter} from './AllocationShared'
import CellSettingsPanel from './CellSettingsPanel'
import HeldByOtherQuestions, {
  heldCellKey,
  heldCellNamed,
  heldCellsWithNames,
} from './HeldByOtherQuestions'
import VersiAllocationPage from './VersiAllocationPage'

/** The ranges the settings endpoint accepts (handoff §4.2). */
const MAX_WEEKLY = 50
const MAX_DAILY = 10
const MAX_CONSECUTIVE = 10

const NO_FACTS = {knownTeacher: null, sample: null}
const NO_HELD: NonNullable<AllocationPrefillResult['heldByOther']> = []
const NO_ANSWERS: DataUploadAllocationAssignment[] = []

/**
 * The prefill's questions that Add Teachers already answered (`answers`, same teacher +
 * subject): sent back as they were, so the prefill places them and does not ask again. A line
 * still `pending` is the server asking anyway (confirmed with no classes): not answered for it.
 */
const answersAlreadyGiven = (
  questions: readonly DataUploadAllocationQuestion[],
  answers: readonly DataUploadAllocationAssignment[]
): DataUploadAllocationAssignment[] => {
  const given = new Map(answers.map((answer) => [allocationAnswerKey(answer), answer]))
  return questions.flatMap((question) =>
    question.subjects.flatMap((entry) => {
      if (!entry.subject || entry.pending) return []
      const answer = given.get(
        allocationAnswerKey({
          teacher: question.teacher,
          employeeId: question.employeeId,
          subject: entry.subject,
        })
      )
      return answer ? [answer] : []
    })
  )
}

/**
 * The level kept when the branch changes before the server answers, by the server's rule
 * (§3.1): the current one when it belongs to the new branch, the branch's only level when it
 * has one, else All.
 */
const levelAfterBranch = (
  scope: AllocationServerScope | null | undefined,
  levelId: number | null | undefined,
  branchId: number | null
) => {
  if (branchId == null) return levelId ?? null
  const ofBranch = scope?.levelOptions.filter((o) => o.branchId === branchId) ?? []
  if (levelId != null && ofBranch.some((o) => o.id === levelId)) return levelId
  return ofBranch.length === 1 ? ofBranch[0].id : null
}

/** Level options for a branch picked a moment ago, from the options already on hand. */
const levelsForPick = (
  choices: ReturnType<typeof scopeChoicesOf>,
  scope: AllocationServerScope | null | undefined,
  branchId: number | null | undefined
) => {
  if (branchId == null || !scope || scope.branchId === branchId) return choices.levels
  const values = scope.levelOptions.filter((o) => o.branchId === branchId).map((o) => String(o.id))
  return values.length > 0 ? {values, hasAll: values.length > 1} : choices.levels
}

const scopeValue = (id: number | null | undefined) => (id == null ? '' : String(id))

export type ServerAllocationTrackEvent =
  | 'manual_assignment'
  | 'ai_assignment'
  /** Versi was opened and left with nothing applied (sent when leaving it). */
  | 'ai_opened_not_used'

type Props = {
  /** This server has no `/onboarding/allocation/*` routes: the step falls back to the client flow. */
  onUnavailable: () => void
  /** Landing page Back: the previous wizard tab. */
  onBack: () => void
  backDisabled?: boolean
  /** Continue, once every requirement is filled. Nothing is pushed: every edit is saved. */
  onContinue: () => void
  continueBusy?: boolean
  uploadFormats: string[]
  uploadDisabled?: boolean
  onUploadFiles: () => void
  /** The upload card's panels (file status, questions, reports). */
  importArea?: ReactNode
  /** The Upload card's body, for the shared uploader (`AllocationLanding.uploadSlotRef`). */
  uploadSlotRef?: (element: HTMLDivElement | null) => void
  guideUrl?: string
  defaultFrequency: string
  /** Bumped after an allocation file was imported: progress and grid are read again. */
  refreshKey: number
  /** `metadata`: the counts sent with the event (`cells`: assignments applied). */
  onTrack: (event: ServerAllocationTrackEvent, metadata?: Record<string, unknown>) => void
  /** Something was saved: the wizard's own copy of the grid is now behind the server. */
  onEdited: () => void
  /**
   * Save and finish later (§7): the wizard calls the registered function first, which closes
   * an open cell-settings panel without saving it (its edits are discarded).
   */
  registerSaveLaterFlush?: (flush: (() => void) | null) => void
  /**
   * The "Which classes?" answers given on Add Teachers this visit. The prefill's questions are
   * only for lines Add Teachers never saw: one it asks again is answered with these, unasked.
   */
  teacherAnswers?: readonly DataUploadAllocationAssignment[]
  /** The overall progress, each time it changes (Review Teachers' "Assignments found" follows it). */
  onOverallChange?: (overall: AllocationProgress) => void
  /**
   * The wizard's teachers (`teacherKeysOf`): the uploaded file's "Which classes?" lines for a
   * teacher no longer in the list are not shown ("N need review", its dialog). Left out, all are.
   */
  teacherKeys?: ReadonlySet<string>
}

type Removal =
  /** The preview is on its way: the dialog is already open, as a skeleton. */
  | {type: 'loading'}
  | {
      type: 'cell'
      subjectId: number
      subject: string
      section: string
      branch: string
      /** The teacher who will be unassigned, if any. */
      teacher: string | null
    }
  | {type: 'server'; request: AllocationRemovalRequest; preview: AllocationRemovalPreview}

/**
 * Teacher Allocation backed by the server (node-api `/onboarding/allocation/*`, the handoff
 * "Teacher Allocation (server-side): portal handoff"). Same screens as the client flow — landing,
 * grid with cell settings and removals, Versi — rendered by the same components; the server owns
 * the data, so every edit is saved as it is made and Continue only navigates.
 */
const ServerTeacherAllocation: FC<Props> = ({
  onUnavailable,
  onBack,
  backDisabled,
  onContinue,
  continueBusy,
  uploadFormats,
  uploadDisabled,
  onUploadFiles,
  importArea,
  uploadSlotRef,
  guideUrl,
  defaultFrequency,
  refreshKey,
  onTrack,
  onEdited,
  registerSaveLaterFlush,
  teacherAnswers = NO_ANSWERS,
  onOverallChange,
  teacherKeys,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const ta = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values)

  const [view, setView] = useState<'landing' | 'grid' | 'ai'>('landing')
  const viewRef = useRef(view)
  viewRef.current = view
  const [gridPreviewMode, setGridPreviewMode] = useState(false)
  /** The branch picked for the uploaded file's records without one, while it is placed. */
  const [prefillPick, setPrefillPick] = useState<number | 'all' | null>(null)
  /**
   * The "Which classes?" questions closed with X, by their key: they stay unanswered, nothing is
   * sent, and the landing offers them again. A new set of questions opens the dialog again.
   */
  const [dismissedPrefillQuestions, setDismissedPrefillQuestions] = useState<string | null>(null)

  // "Opened, not used": set when Versi opens, cleared when a proposal is applied. Leaving Versi
  // with it still set (landing, the grid outside preview, Continue, Back, unmount) reports it
  // once. The proposal preview grid is still the Versi session, so it is not leaving.
  const versiUnusedRef = useRef(false)
  const onTrackRef = useRef(onTrack)
  onTrackRef.current = onTrack
  const leaveVersi = () => {
    if (!versiUnusedRef.current) return
    versiUnusedRef.current = false
    onTrackRef.current('ai_opened_not_used')
  }
  const leaveVersiRef = useRef(leaveVersi)
  leaveVersiRef.current = leaveVersi
  useEffect(() => {
    if (view === 'landing' || (view === 'grid' && !gridPreviewMode)) leaveVersiRef.current()
  }, [view, gridPreviewMode])
  useEffect(() => () => leaveVersiRef.current(), [])
  const [aiScope, setAiScope] = useState<AllocationScopeQuery>({})
  const [aiScopeInfo, setAiScopeInfo] = useState<AllocationServerScope | null>(null)
  const [settingsFor, setSettingsFor] = useState<number | null>(null)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [removal, setRemoval] = useState<Removal | null>(null)
  const [removalBusy, setRemovalBusy] = useState(false)
  /** Bumped when a removal preview is cancelled, so its late answer opens nothing. */
  const removalRequestRef = useRef(0)
  /**
   * The Branch / Level just picked on the grid, shown in the dropdowns at once while its rows
   * load (the skeleton); the server's resolution replaces it when the answer lands.
   */
  const [gridPick, setGridPick] = useState<AllocationScopeQuery | null>(null)
  /** Cells a Versi proposal just changed (green) or would have (red), flashed on the grid. */
  const [flash, setFlash] = useState<AllocationGridFlash | null>(null)
  const flashSeqRef = useRef(0)
  const startFlash = (slotKeys: string[] | undefined, tone: AllocationGridFlash['tone']) => {
    if (!slotKeys || slotKeys.length === 0) return
    flashSeqRef.current += 1
    setFlash({slots: new Set(slotKeys), tone, key: flashSeqRef.current})
  }

  // A dropped connection (statusCode 0) carries the browser's own text, "Failed to fetch". The
  // message is the toast's id: a failed pick and the reload after it would otherwise show it twice.
  const showError = (error: AllocationServerError) => {
    const message = (error.statusCode !== 0 && error.message) || ta('ERROR')
    toast.error(message, {toastId: message})
  }

  const undoRef = useRef<(batchId: string) => void>(() => undefined)
  const reopenVersiRef = useRef<() => void>(() => undefined)
  const undoToast = (message: string, batchId: string | null) =>
    toast.info(
      ({closeToast}: {closeToast?: () => void}) => (
        <span className='ta-toast'>
          <span>{message}</span>
          {batchId && (
            <button
              type='button'
              className='ta-toast__undo'
              onClick={() => {
                undoRef.current(batchId)
                closeToast?.()
              }}
            >
              {ta('UNDO')}
            </button>
          )}
        </span>
      ),
      {autoClose: 8000, className: 'ta-toast-wide'}
    )

  const server = useAllocationServer({
    onUnavailable,
    onPrefillApplied: ({filled, batchId, alreadyAssigned, heldByOther}) =>
      undoToast(
        // Never "N filled" as if everything went in: cells another teacher keeps are said too
        // ("30 assignments: 4 new, 24 already assigned, 2 kept by another teacher").
        heldByOther > 0
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT_HELD'},
              {
                total: filled + alreadyAssigned + heldByOther,
                already: alreadyAssigned,
                fresh: filled,
                held: heldByOther,
              }
            )
          : alreadyAssigned > 0
            ? intl.formatMessage(
                {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT'},
                {total: filled + alreadyAssigned, already: alreadyAssigned, fresh: filled}
              )
            : ta('PREFILL.FILLED', {count: filled}),
        batchId
      ),
    onEdited,
    onError: showError,
    onPreviewGone: () => {
      setGridPreviewMode(false)
      reopenVersiRef.current()
    },
  })

  const onOverallChangeRef = useRef(onOverallChange)
  onOverallChangeRef.current = onOverallChange
  useEffect(() => {
    if (server.overall) onOverallChangeRef.current?.(server.overall)
  }, [server.overall])
  // A re-upload's cells another teacher already has (`heldByOther`, AI server): never replaced
  // by the prefill. Asked one by one on the landing. The server names them and says why
  // (`holder`, `message`); only an older server's bare ids need a whole-school grid for names.
  const heldByOther = server.prefill?.heldByOther ?? NO_HELD
  const [heldGrid, setHeldGrid] = useState<AllocationServerGrid | null>(null)
  const [heldGridAsked, setHeldGridAsked] = useState(false)
  useEffect(() => {
    if (heldByOther.length === 0 || heldGridAsked || heldByOther.every(heldCellNamed)) return
    setHeldGridAsked(true)
    allocationServerApi.grid(dispatch, undefined, 'assigned').then(setHeldGrid, () => undefined)
  }, [dispatch, heldByOther, heldGridAsked])
  /**
   * Held cells answered (Keep or Replace), by `subjectId|teacherId`: kept here, so switching to the
   * grid or Versi and back does not ask them again (QA 552).
   */
  const [heldAnswered, setHeldAnswered] = useState<ReadonlySet<string>>(() => new Set())
  const heldCells = useMemo(() => {
    // The cells as the grid has them now: a question whose cell no longer has that holder (the
    // user changed or cleared it, e.g. "unassign all") has nothing left to ask. Cells the loaded
    // grid does not show keep their question.
    const teacherNow = new Map<number, number | null>()
    server.grid?.branches.forEach((branch) =>
      branch.rows.forEach((row) =>
        Object.values(row.cells).forEach((cell) => teacherNow.set(cell.subjectId, cell.teacherId))
      )
    )
    return heldCellsWithNames(heldByOther, heldGrid).filter(
      (cell) => !teacherNow.has(cell.subjectId) || teacherNow.get(cell.subjectId) === cell.holderId
    )
  }, [heldByOther, heldGrid, server.grid])
  undoRef.current = (batchId) => {
    server.undo(batchId).catch(() => undefined)
  }

  // Save and finish later: an open settings panel is discarded first (§7).
  useEffect(() => {
    if (!registerSaveLaterFlush) return
    registerSaveLaterFlush(() => setSettingsFor(null))
    return () => registerSaveLaterFlush(null)
  }, [registerSaveLaterFlush])

  const versi = useVersiServer({
    scope: aiScope,
    columnNames: server.view.columnNames,
    onScopeChange: setAiScope,
    // A proposal discarded from the grid preview: its cells flash red.
    onDiscarded: (slotKeys) => {
      if (viewRef.current === 'grid') startFlash(slotKeys, 'discarded')
    },
    onApplied: (overall, canContinue, slotKeys, undone, applied) => {
      // Shown the next time the grid is on screen (at once when applied from the preview).
      startFlash(slotKeys, 'applied')
      // Used: leaving Versi now reports nothing.
      versiUnusedRef.current = false
      server.takeProgress(overall, canContinue)
      server.refresh()
      onEdited()
      // Only a real apply is an AI allocation; an Undo takes one back.
      if (!undone) onTrack('ai_assignment', applied != null ? {cells: applied} : undefined)
    },
  })

  reopenVersiRef.current = versi.open

  // The flash runs once the grid's rows are on screen, then goes (the earlier grid's 3.5s).
  useEffect(() => {
    if (!flash || view !== 'grid' || server.gridLoading === 'scope') return
    const timer = window.setTimeout(() => setFlash(null), 3500)
    return () => window.clearTimeout(timer)
  }, [flash, view, server.gridLoading])

  // Leaving the proposal preview: the grid goes back to the saved cells.
  useEffect(() => {
    if (!gridPreviewMode && server.preview) server.loadGrid(undefined, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on leaving preview only
  }, [gridPreviewMode])

  // An allocation file was imported: read progress and grid again.
  const firstRefreshRef = useRef(true)
  useEffect(() => {
    if (firstRefreshRef.current) {
      firstRefreshRef.current = false
      return
    }
    server.refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the key moves
  }, [refreshKey])

  // Versi's dropdowns follow the server's resolution of the chosen Branch / Level (§3.1).
  useEffect(() => {
    if (view !== 'ai') return
    let live = true
    allocationServerApi
      .summary(dispatch, aiScope)
      .then((res) => {
        if (!live) return
        setAiScopeInfo(res.scope)
        if (
          res.scope.branchId !== (aiScope.branchId ?? null) ||
          res.scope.levelId !== (aiScope.levelId ?? null)
        ) {
          setAiScope({branchId: res.scope.branchId, levelId: res.scope.levelId})
        }
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [aiScope.branchId, aiScope.levelId, dispatch, view])

  // Leaving preview once the proposal is gone (applied, discarded or refreshed).
  useEffect(() => {
    if (gridPreviewMode && !versi.pendingItem && !versi.isApplying) setGridPreviewMode(false)
  }, [gridPreviewMode, versi.isApplying, versi.pendingItem])

  const gridScope = server.grid?.scope
  const gridChoices = useMemo(() => scopeChoicesOf(gridScope), [gridScope])
  // The picked Branch / Level shows at once; the server's answer replaces it.
  useEffect(() => {
    if (server.gridLoading === null) setGridPick(null)
  }, [server.gridLoading])
  const shownGridScope: AllocationScopeQuery =
    gridPick ?? (gridScope ? {branchId: gridScope.branchId, levelId: gridScope.levelId} : {})
  const pickGridScope = (next: AllocationScopeQuery) => {
    setGridPick(next)
    server.loadGrid(next)
  }
  const aiScopeShown = aiScopeInfo ?? versi.serverScope ?? gridScope
  const aiChoices = useMemo(
    () => scopeChoicesOf(aiScopeInfo ?? versi.serverScope ?? gridScope),
    [aiScopeInfo, gridScope, versi.serverScope]
  )
  const counts = countsOf(server.overall)
  const {view: grid} = server

  const openGrid = (options?: {preview?: boolean}) => {
    const scope = options?.preview
      ? (versi.pendingProposal?.scope ?? aiScope)
      : view === 'ai'
        ? aiScope
        : undefined
    setGridPreviewMode(Boolean(options?.preview))
    setView('grid')
    server.loadGrid(scope, options?.preview ? (versi.pendingProposal?.id ?? null) : null)
  }

  const openVersi = (fromGrid: boolean) => {
    // From the grid the working scope carries over; from the landing page it starts at All.
    setAiScope(
      fromGrid && gridScope ? {branchId: gridScope.branchId, levelId: gridScope.levelId} : {}
    )
    setGridPreviewMode(false)
    setView('ai')
    versiUnusedRef.current = true
    versi.open()
  }

  const backToMethods = () => {
    setGridPreviewMode(false)
    setSettingsFor(null)
    setView('landing')
    server.refreshSummary()
  }

  const footer = (onLanding: boolean) => (
    <AllocationFooter
      backLabel={onLanding ? ta('BACK') : ta('BACK_TO_METHODS')}
      backVariant={onLanding ? 'solid' : 'outline'}
      onBack={
        onLanding
          ? () => {
              leaveVersi()
              onBack()
            }
          : backToMethods
      }
      backDisabled={onLanding && backDisabled}
      canContinue={server.canContinue}
      hint={onLanding ? ta('CONTINUE_HINT') : ta('CONTINUE_HINT_REMAINING')}
      busy={continueBusy}
      onContinue={() => {
        leaveVersi()
        onContinue()
      }}
    />
  )

  // --- Removals (§5): preview, confirm, apply with the previewed count -------------------
  const startRemoval = (request: AllocationRemovalRequest, subjectLabel?: string) => {
    if (removalBusy || removal) return
    const requestId = (removalRequestRef.current += 1)
    const current = () => requestId === removalRequestRef.current
    setRemoval({type: 'loading'})
    server
      .previewRemoval(request)
      .then((preview) => {
        if (!current()) return
        if (preview.count === 0) {
          setRemoval(null)
          toast.info(
            ta('REMOVE.NOTHING_TO_REMOVE', {subject: subjectLabel ?? preview.subjectName ?? ''})
          )
          return
        }
        setRemoval({type: 'server', request, preview})
      })
      .catch(() => {
        if (current()) setRemoval(null)
      })
  }

  const cancelRemoval = () => {
    if (removalBusy) return
    removalRequestRef.current += 1
    setRemoval(null)
  }

  const confirmRemoval = () => {
    const current = removal
    if (!current || current.type === 'loading' || removalBusy) return
    setRemovalBusy(true)
    if (current.type === 'cell') {
      server
        .markNotTaught(current.subjectId)
        .then((batchId) => {
          setRemoval(null)
          setSettingsFor(null)
          undoToast(
            ta('REMOVE.DONE_CELL', {subject: current.subject, section: current.section}),
            batchId
          )
        })
        .catch(() => setRemoval(null))
        .finally(() => setRemovalBusy(false))
      return
    }
    const {request, preview} = current
    server
      .applyRemoval(request, preview.count)
      .then((res) => {
        setRemoval(null)
        const message =
          preview.kind === 'row'
            ? ta('REMOVE.DONE_ROW', {section: preview.className ?? ''})
            : preview.kind === 'column'
              ? ta('REMOVE.DONE_COLUMN', {subject: preview.subjectName ?? '', count: res.count})
              : ta('REMOVE.DONE_CELL', {
                  subject: preview.items[0]?.subjectName ?? '',
                  section: preview.items[0]?.className ?? '',
                })
        undoToast(message, res.batchId)
      })
      .catch((error: AllocationServerError) => {
        if (error.code !== 'REMOVAL_CHANGED') {
          setRemoval(null)
          return
        }
        // The affected cells moved since the preview: nothing was removed. Preview again.
        toast.info(ta('REMOVE.CHANGED'))
        return server.previewRemoval(request).then(
          (next) => setRemoval(next.count > 0 ? {type: 'server', request, preview: next} : null),
          () => setRemoval(null)
        )
      })
      .finally(() => setRemovalBusy(false))
  }

  const removalDialog = (() => {
    if (!removal) return null
    if (removal.type === 'loading') return {title: '', messages: [], confirm: ''}
    if (removal.type === 'cell') {
      const values = {subject: removal.subject, section: removal.section, branch: removal.branch}
      return {
        title: ta('REMOVE.CELL_TITLE', values),
        messages: [
          [
            ta('REMOVE.CELL_TEXT', values),
            // Handover 3C wording: the assignment, not the teacher's name.
            removal.teacher ? ta('REMOVE.CELL_TEXT_TEACHER') : '',
            ta('REMOVE.CELL_TEXT_KEEP', values),
          ]
            .filter(Boolean)
            .join(' '),
        ],
        confirm: ta('REMOVE.CELL_CONFIRM'),
      }
    }
    const {preview} = removal
    if (preview.kind === 'row') {
      const values = {section: preview.className ?? '', branch: preview.items[0]?.branch ?? ''}
      // Handover 3C: the section message alone — no subject count or list.
      return {
        title: ta('REMOVE.ROW_TITLE', values),
        messages: [ta('REMOVE.ROW_TEXT', values)],
        confirm: ta('REMOVE.ROW_CONFIRM'),
      }
    }
    if (preview.kind === 'cell') {
      const item = preview.items[0]
      const values = {
        subject: item?.subjectName ?? '',
        section: item?.className ?? '',
        branch: item?.branch ?? '',
      }
      return {
        title: ta('REMOVE.CELL_TITLE', values),
        messages: [
          [
            ta('REMOVE.CELL_TEXT', values),
            item?.teacher ? ta('REMOVE.CELL_TEXT_TEACHER') : '',
            ta('REMOVE.CELL_TEXT_KEEP', values),
          ]
            .filter(Boolean)
            .join(' '),
        ],
        confirm: ta('REMOVE.CELL_CONFIRM'),
      }
    }
    const scope = `${preview.scope.branch ?? ta('REMOVE.ALL_BRANCHES')} · ${
      preview.scope.level ?? ta('REMOVE.ALL_LEVELS')
    }`
    const values = {subject: preview.subjectName ?? '', count: preview.count, scope}
    const byFilter = {
      all: ['REMOVE.COLUMN_TITLE_ALL', 'REMOVE.COLUMN_TEXT_ALL'],
      assigned: ['REMOVE.COLUMN_TITLE_ASSIGNED', 'REMOVE.COLUMN_TEXT_ASSIGNED'],
      unassigned: ['REMOVE.COLUMN_TITLE_UNASSIGNED', 'REMOVE.COLUMN_TEXT_UNASSIGNED'],
    }[preview.filter]
    // Handover 3C: the title, the message (plus the Unassigned add-on) and the buttons — nothing
    // else. The message already gives the count and says affected teacher assignments go too.
    return {
      title: ta(byFilter[0], values),
      messages: [
        ta(byFilter[1], values),
        ...(preview.filter === 'unassigned'
          ? [ta('REMOVE.COLUMN_TEXT_KEEP_ASSIGNED', values)]
          : []),
      ],
      confirm: ta('REMOVE.COLUMN_CONFIRM'),
    }
  })()

  // --- Grid ---------------------------------------------------------------------------------
  const subjectIdFor = (row: AllocationRow, subject: string) =>
    grid.subjectIdBySlot.get(slotKeyFor(row, subject)) ?? null

  const preview =
    gridPreviewMode && versi.pendingItem
      ? {
          changes: versi.previewChanges,
          count: versi.pendingItem.changes.length,
          outside:
            server.preview?.outsideView.length ??
            Array.from(versi.previewChanges.keys()).filter(
              (slot) => !grid.subjectIdBySlot.has(slot)
            ).length,
          isApplying: versi.isApplying,
          onApply: async () => {
            const outcome = await versi.apply()
            // A refreshed proposal is waiting in the conversation.
            if (outcome === 'stale') setView('ai')
            if (outcome !== 'noop') setGridPreviewMode(false)
          },
          onBackToConversation: () => {
            setGridPreviewMode(false)
            setView('ai')
          },
          onDiscard: () => {
            versi.discard()
            setGridPreviewMode(false)
          },
        }
      : null

  const settingsCell = settingsFor !== null ? grid.cellBySubjectId.get(settingsFor) : undefined
  const settingsSlot = settingsFor !== null ? grid.slotBySubjectId.get(settingsFor) : undefined
  const settingsPlace = settingsSlot ? splitSlotKey(settingsSlot) : null
  const settingsRow = settingsPlace ? grid.rowByClassKey.get(settingsPlace.classKey) : undefined
  const settingsFrequency = settingsCell
    ? frequencyOf(settingsCell.weeklyPeriods, settingsCell.dailyPeriods) || defaultFrequency
    : defaultFrequency
  const settingsDuration = String(settingsCell?.consecutivePeriods || 1)
  /** The stored frequency can't be read: shown as "–" until the user sets it (§3.2). */
  const settingsUnset =
    Boolean(settingsCell) && !frequencyOf(settingsCell!.weeklyPeriods, settingsCell!.dailyPeriods)

  const saveSettings = ({frequency, duration}: {frequency: string; duration: string}) => {
    if (settingsFor === null) return
    const before = parseFrequency(settingsFrequency, defaultFrequency)
    const after = parseFrequency(frequency, defaultFrequency)
    const body: {weeklyPeriods?: number; dailyPeriods?: number; consecutivePeriods?: number} = {}
    if (
      settingsUnset ||
      formatFrequency(after.count, after.per) !== formatFrequency(before.count, before.per)
    ) {
      if (after.per === 'day') body.dailyPeriods = after.count
      else body.weeklyPeriods = after.count
    }
    if (parseDuration(duration) !== parseDuration(settingsDuration)) {
      body.consecutivePeriods = parseDuration(duration)
    }
    if (Object.keys(body).length === 0) {
      setSettingsFor(null)
      return
    }
    setSettingsSaving(true)
    server
      .saveSettings(settingsFor, body)
      .then(() => setSettingsFor(null))
      // Refused: the panel stays open with the values the user typed.
      .catch(() => undefined)
      .finally(() => setSettingsSaving(false))
  }

  // --- Landing: the uploaded file's lines (§2.1) ------------------------------------------
  const prefill = server.prefill
  const prefillPanel =
    prefill?.state === 'still_reading' ? (
      <div className='ta-card ta-server-prefill' role='status' aria-live='polite'>
        <span className='spinner-border spinner-border-sm text-primary' aria-hidden />
        <span>{ta('PREFILL.READING')}</span>
      </div>
    ) : prefill?.state === 'needs_branch' && (prefill.branches?.length ?? 0) > 0 ? (
      <div className='ta-card ta-server-prefill'>
        <p>
          {ta('PREFILL.NEEDS_BRANCH', {filled: prefill.filled ?? 0, waiting: prefill.waiting ?? 0})}
        </p>
        <div
          className='ta-choices'
          role='group'
          aria-label={ta('PREFILL.NEEDS_BRANCH_ARIA')}
          aria-busy={server.answeringPrefill || undefined}
        >
          {[
            ...prefill.branches!.map((option) => ({key: option.branchId, label: option.branch})),
            {key: 'all' as const, label: ta('PREFILL.ALL_BRANCHES')},
          ].map((choice) => {
            // The picked branch spins while its records are placed (it takes a moment).
            const busy = server.answeringPrefill && prefillPick === choice.key
            return (
              <button
                key={choice.key}
                type='button'
                className='ta-choice'
                disabled={server.answeringPrefill}
                onClick={() => {
                  setPrefillPick(choice.key)
                  server.answerPrefillBranch(choice.key)
                }}
              >
                {busy && (
                  <span className='spinner-border spinner-border-sm me-2' aria-hidden />
                )}
                {choice.label}
              </button>
            )
          })}
        </div>
        {server.answeringPrefill && (
          <p className='ta-server-prefill__busy' role='status'>
            {ta('PREFILL.PLACING')}
          </p>
        )}
      </div>
    ) : null

  // A re-upload with nothing new to place: said on the landing (not a toast: the prefill runs on
  // every visit until something is placed) — "30 assignments: 30 already assigned, 0 new".
  const prefillAlready = prefill?.state === 'nothing_placed' ? (prefill.alreadyAssigned ?? 0) : 0
  const prefillSplitNote =
    prefillAlready > 0 ? (
      <div className='ta-card ta-server-prefill' role='status'>
        <span>
          {intl.formatMessage(
            heldByOther.length > 0
              ? {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT_HELD'}
              : {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT'},
            {
              total: prefillAlready + heldByOther.length,
              already: prefillAlready,
              fresh: 0,
              held: heldByOther.length,
            }
          )}
        </span>
      </div>
    ) : null
  const heldQuestions = (
    <HeldByOtherQuestions
      cells={heldCells}
      answered={heldAnswered}
      onAnswered={(cell) => setHeldAnswered((prev) => new Set(prev).add(heldCellKey(cell)))}
      onReplace={(cell) =>
        allocationServerApi.setTeacher(dispatch, cell.subjectId, cell.teacherId).then(
          () => server.refresh(),
          (error: AllocationServerError) => {
            showError(error)
            return Promise.reject(error)
          }
        )
      }
    />
  )

  // The Teachers file's lines the prefill could not place on its own (no class, or a class with
  // several sections): asked in the same "Which classes?" dialog as an upload, and only those.
  // Skip answers each with no class, so it is not allocated and not asked again.
  // Lines confirmed with no classes (`pending`) come marked in `questions`, or apart in
  // `pending`; either way they wait behind "N need review" and are never opened by themselves.
  const prefillAll = useMemo(() => {
    const asked =
      (prefill?.asked ?? 0) > 0 || (prefill?.questions?.length ?? 0) > 0
        ? normalizeAllocationQuestions({allocationQuestions: prefill?.questions})
        : []
    const pending = normalizeAllocationQuestions({allocationQuestions: prefill?.pending}).map(
      (question) => ({
        ...question,
        subjects: question.subjects.map((entry) => ({...entry, pending: true})),
      })
    )
    const known = new Set(asked.map((question) => question.key))
    const all = [...asked, ...pending.filter((question) => !known.has(question.key))]
    // A teacher deleted since the file was read has nothing left to assign: not shown.
    return teacherKeys ? allocationQuestionsForTeachers(all, teacherKeys) : all
  }, [prefill, teacherKeys])
  // Only lines Add Teachers never saw are asked here: one it answered goes back with that answer,
  // once. Asked again after that (the answer did not place it), it is shown like the rest.
  const alreadyGiven = useMemo(
    () => answersAlreadyGiven(prefillAll, teacherAnswers),
    [prefillAll, teacherAnswers]
  )
  const alreadyGivenKey = alreadyGiven.map(allocationAnswerKey).join('|')
  const [sentGivenKey, setSentGivenKey] = useState('')
  useEffect(() => {
    if (!alreadyGivenKey || sentGivenKey === alreadyGivenKey || server.answeringPrefill) return
    setSentGivenKey(alreadyGivenKey)
    server.answerPrefillQuestions(alreadyGiven)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per set of known answers
  }, [alreadyGivenKey, server.answeringPrefill])
  const prefillQuestions =
    alreadyGivenKey && sentGivenKey !== alreadyGivenKey
      ? withoutAnsweredAllocations(prefillAll, alreadyGiven)
      : prefillAll
  /** Lines that need classes chosen: these open the dialog. Review-only ones do not. */
  const prefillAskable = askableAllocations(prefillQuestions)
  const prefillLines = allocationLineCounts(prefillQuestions)
  // Some servers only count the lines waiting for review (`awaitingReview`).
  // With no teacher left in the list there is nobody to review for.
  const prefillReviewCount =
    prefillLines.review || (teacherKeys?.size === 0 ? 0 : (prefill?.awaitingReview ?? 0))
  const prefillQuestionsKey = prefillQuestions.map((question) => question.key).join('|')
  const prefillQuestionsDismissed =
    prefillAskable.length > 0 && dismissedPrefillQuestions === prefillQuestionsKey
  /** "N need review" (or a dismissed set) opened on purpose. */
  const [prefillReviewOpen, setPrefillReviewOpen] = useState(false)
  const prefillDialogShown =
    prefillQuestions.length > 0 &&
    (prefillReviewOpen || (prefillAskable.length > 0 && !prefillQuestionsDismissed))
  const prefillQuestionsPending =
    !prefillDialogShown && (prefillQuestionsDismissed || prefillReviewCount > 0) ? (
      <div className='ta-card ta-server-prefill' role='status'>
        <span>
          {/* "3 teachers (5 subjects) need classes · 2 need review": who, not lines. */}
          {pendingQuestionsText(intl.formatMessage, {allocations: prefillQuestions}) ??
            (prefillReviewCount > 0
              ? intl.formatMessage(
                  {id: 'DATA_UPLOAD.QUESTIONS.LINES_NEED_REVIEW'},
                  {count: prefillReviewCount}
                )
              : intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.WAITING'}))}
        </span>
        {prefillQuestions.length > 0 && (
          <button
            type='button'
            className='ta-btn ta-btn--outline ta-btn--sm'
            onClick={() => setPrefillReviewOpen(true)}
          >
            {intl.formatMessage({
              id: prefillAskable.length > 0
                ? 'DATA_UPLOAD.QUESTIONS.ANSWER_SHORT'
                : 'DATA_UPLOAD.QUESTIONS.REVIEW_SHORT',
            })}
          </button>
        )}
      </div>
    ) : null
  const prefillQuestionsDialog = (
    <ColumnAssignmentModal
      show={prefillDialogShown}
      columns={null}
      branches={[]}
      subjects={null}
      allocations={prefillQuestions}
      proposalReview={ONBOARDING_QA_OCT18}
      submitting={server.answeringPrefill}
      onConfirm={(answers) => {
        setPrefillReviewOpen(false)
        server.answerPrefillQuestions(answers.allocations)
      }}
      onSkip={() => {
        setPrefillReviewOpen(false)
        server.answerPrefillQuestions(skippedAllocationAnswers(prefillQuestions))
      }}
      onClose={() => {
        setPrefillReviewOpen(false)
        setDismissedPrefillQuestions(prefillQuestionsKey)
      }}
    />
  )
  // Lines the school has no subject for: nothing to pick, said in the server's words.
  const prefillUnassignable = (prefill?.skipped ?? []).filter(
    (line) => (line.reason ?? line.code) === 'SUBJECT_NOT_IN_SCHOOL'
  )
  const prefillUnassignableNote =
    prefillUnassignable.length > 0 ? (
      <div className='ta-card ta-server-prefill d-block' role='status'>
        <p className='mb-1 fw-semibold'>
          {intl.formatMessage(
            {id: 'DATA_UPLOAD.QUESTIONS.LINES_CANT_BE_ASSIGNED'},
            {count: prefillUnassignable.length}
          )}
        </p>
        <ul className='mb-0 ps-4'>
          {prefillUnassignable.map((line, index) => (
            <li key={`${line.teacher ?? ''}-${line.subject ?? line.written ?? ''}-${index}`}>
              {line.message ||
                [line.teacher, line.subject ?? line.written].filter(Boolean).join(' · ')}
            </li>
          ))}
        </ul>
      </div>
    ) : null

  const loadError =
    server.status === 'error' ? (
      <div className='ta-card ta-server-prefill' role='alert'>
        <span>{ta('LOAD_ERROR')}</span>
        <button
          type='button'
          className='ta-btn ta-btn--outline ta-btn--sm'
          onClick={server.refreshSummary}
        >
          {ta('RETRY')}
        </button>
      </div>
    ) : null

  if (server.status === 'unavailable') return null

  const rowByClassKey = new Map(grid.rowByClassKey)
  versi.proposalRows.forEach((row) => {
    if (!rowByClassKey.has(row.classKey)) rowByClassKey.set(row.classKey, row)
  })

  return (
    <>
      {prefillQuestionsDialog}
      {view === 'landing' && (
        <AllocationLanding
          counts={counts}
          prefilledCount={server.prefilledCount}
          uploadFormats={uploadFormats}
          uploadDisabled={uploadDisabled}
          guideUrl={guideUrl}
          loading={server.status === 'loading' || server.summaryLoading}
          selectableMethods
          onOpenGrid={() => openGrid()}
          onOpenAi={() => openVersi(false)}
          onUploadFiles={onUploadFiles}
          uploadSlotRef={uploadSlotRef}
          importArea={
            <>
              {loadError}
              {prefillPanel}
              {prefillQuestionsPending}
              {prefillUnassignableNote}
              {prefillSplitNote}
              {heldQuestions}
              {importArea}
            </>
          }
          footer={footer(true)}
        />
      )}

      {view === 'grid' && (
        <AllocationGrid
          rows={grid.rows}
          grid={grid.grid}
          deleted={grid.deleted}
          teachers={grid.teachers}
          importedSlots={grid.imported}
          counts={counts}
          hasImports={server.prefilledCount > 0}
          branch={scopeValue(shownGridScope.branchId)}
          level={scopeValue(shownGridScope.levelId)}
          scopeOptions={{
            branches: gridChoices.branches,
            levels: gridPick
              ? levelsForPick(gridChoices, gridScope, gridPick.branchId)
              : gridChoices.levels,
            branchLabel: gridChoices.branchLabel,
            groupByBranch: Boolean(server.grid?.groupByBranch),
          }}
          loading={server.gridLoading === 'scope'}
          refreshing={server.gridLoading === 'refresh'}
          changeAnimations
          flash={view === 'grid' ? flash : null}
          onBranchChange={(value) => {
            const branchId = scopeIdOf(value)
            pickGridScope({
              branchId,
              levelId: levelAfterBranch(gridScope, shownGridScope.levelId, branchId),
            })
          }}
          onLevelChange={(value) =>
            pickGridScope({branchId: shownGridScope.branchId ?? null, levelId: scopeIdOf(value)})
          }
          levelLabel={gridChoices.levelLabel}
          defaultFrequency={defaultFrequency}
          activeSlotKey={settingsSlot ?? null}
          preview={preview}
          onSelectTeacher={(row, subject, teacher) => {
            const subjectId = subjectIdFor(row, subject)
            if (subjectId === null || (teacher && teacher.id == null)) return
            if (teacher) onTrack('manual_assignment')
            server.setTeacher(subjectId, teacher?.id ?? null)
          }}
          onOpenSettings={(row, subject) => setSettingsFor(subjectIdFor(row, subject))}
          onRemoveColumn={(subject: string, filter: AllocationStatusFilter) =>
            startRemoval(
              {
                kind: 'column',
                subjectName: subject,
                filter,
                branchId: gridScope?.branchId ?? null,
                levelId: gridScope?.levelId ?? null,
              },
              subject
            )
          }
          onRemoveRow={(row) => {
            const classId = classIdOf(row.classKey)
            if (classId !== null) {
              startRemoval({
                kind: 'row',
                classId,
                filter: 'all',
                branchId: gridScope?.branchId ?? null,
                levelId: gridScope?.levelId ?? null,
              })
            }
          }}
          onOpenAi={() => openVersi(true)}
          footer={footer(false)}
        />
      )}

      {view === 'ai' && (
        <VersiAllocationPage
          versi={versi}
          counts={counts}
          facts={NO_FACTS}
          examples={versi.examples}
          lockSingleOption
          flagLowConfidence
          loadingConversation={versi.isStarting}
          rowByClassKey={rowByClassKey}
          branchOptions={aiChoices.branches.values}
          levelOptions={levelsForPick(aiChoices, aiScopeShown, aiScope.branchId).values}
          branch={aiScope.branchId == null ? '' : String(aiScope.branchId)}
          level={aiScope.levelId == null ? '' : String(aiScope.levelId)}
          levelLabel={aiChoices.levelLabel}
          branchLabel={aiChoices.branchLabel}
          defaultFrequency={defaultFrequency}
          onBranchChange={(value) => {
            const branchId = scopeIdOf(value)
            setAiScope({
              branchId,
              levelId: levelAfterBranch(aiScopeShown, aiScope.levelId, branchId),
            })
          }}
          onLevelChange={(value) =>
            setAiScope({branchId: aiScope.branchId ?? null, levelId: scopeIdOf(value)})
          }
          onViewGrid={() => openGrid()}
          onPreviewInGrid={() => openGrid({preview: true})}
          footer={footer(false)}
        />
      )}

      <CellSettingsPanel
        open={Boolean(settingsCell) && view === 'grid'}
        subject={settingsPlace?.subject ?? ''}
        classLabel={settingsRow?.label ?? ''}
        branch={settingsRow?.branch ?? ''}
        notTaught={settingsCell?.state === 'not_taught'}
        frequency={settingsFrequency}
        duration={settingsDuration}
        readOnly={Boolean(preview)}
        maxWeekly={MAX_WEEKLY}
        maxDaily={MAX_DAILY}
        maxConsecutive={MAX_CONSECUTIVE}
        saving={settingsSaving}
        frequencyUnset={settingsUnset}
        schoolDays={settingsCell?.schoolDays}
        onClose={() => setSettingsFor(null)}
        onSave={saveSettings}
        onMarkNotTaught={() => {
          if (!settingsCell || !settingsPlace) return
          setRemoval({
            type: 'cell',
            subjectId: settingsCell.subjectId,
            subject: settingsPlace.subject,
            section: settingsRow?.label ?? '',
            branch: settingsRow?.branch ?? '',
            teacher:
              settingsCell.state === 'assigned' ? (settingsCell.teacher?.name ?? null) : null,
          })
        }}
        onRestore={() => {
          if (!settingsCell) return
          const subject = settingsPlace?.subject ?? ''
          const section = settingsRow?.label ?? ''
          setSettingsSaving(true)
          server
            .restore(settingsCell.subjectId)
            .then(({batchId, cell}) => {
              setSettingsFor(null)
              // Says what actually came back: the earlier teacher, or a requirement to fill.
              const teacher = cell?.state === 'assigned' ? cell.teacher?.name : null
              undoToast(
                teacher
                  ? ta('REMOVE.DONE_RESTORE_TEACHER', {subject, section, teacher})
                  : ta('REMOVE.DONE_RESTORE', {subject, section}),
                batchId
              )
            })
            .catch(() => undefined)
            .finally(() => setSettingsSaving(false))
        }}
      />

      <AllocationConfirmDialog
        open={Boolean(removalDialog)}
        title={removalDialog?.title ?? ''}
        messages={removalDialog?.messages ?? []}
        confirmLabel={removalDialog?.confirm ?? ''}
        loading={removal?.type === 'loading'}
        busy={removalBusy}
        onCancel={cancelRemoval}
        onConfirm={confirmRemoval}
      />
    </>
  )
}

export default ServerTeacherAllocation
